import React, { useEffect, useRef, useState } from 'react';
import { 
    ConnectTacoWebSocket, 
    SetTacoSocketMessageHandler, 
    ClearTacoSocketMessageHandler, 
    DisconnectTacoWebSocket, 
    SendTacoTaps 
} from '../../../utilities/BL/Tacos';
import { GoalBase } from '../../Admin/Commons/GoalBase';

/**
 * COMPONENTE AFICIONADO: TACO TAP CHALLENGE
 */
const Package = () => {
    // ==========================================================================
    // ESTADOS DE JUGADOR Y RED
    // ==========================================================================
    const [playerId, setPlayerId] = useState(() => sessionStorage.getItem('taco.player') || '');
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');

    // ==========================================================================
    // ESTADOS DEL JUEGO (Sincronizados con el Servidor)
    // ==========================================================================
    const [matchState, setMatchState] = useState({
        matchId: null,
        phase: 'WAITING', // WAITING, RUNNING, FINISHED
        winner: 'none'
    });

    // Estados visuales locales (para respuesta inmediata)
    const [localScore, setLocalScore] = useState(0);
    const [localStep, setLocalStep] = useState(1);
    
    // Referencias de alto rendimiento
    const tacoImageRef = useRef(null);
    const tapBufferRef = useRef({ count: 0, timer: null, isSending: false });

    // Configuración
    const BATCH_SIZE = 5;
    const FLUSH_MS = 120;
    const MAXIMUM_TACO_STEPS = 4;
    const FLOATING_EMOJIS = ['📦', '🔥', '✨', '⚽', '🥅', '🧤'];
    
    // ==========================================================================
    // RUTAS DINÁMICAS
    // Dependiendo de quién se conectó, pintamos a su personaje
    // ==========================================================================
    const ASSETS = {
        character: playerId === 'player2' ? '/img/games/taco/barrio_9/tunita-empacadora.png' : '/img/games/taco/barrio_9/lucho-empacador.png',
        tacoStepTemplate: (stepNumber) => `/img/games/taco/barrio_9/${stepNumber}.png` 
    };

    // ==========================================================================
    // CONEXIÓN WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        if (!playerId) return;

        const showToast = (m) => { setToastMessage(m); setTimeout(() => setToastMessage(''), 2500); };
        
        ConnectTacoWebSocket(playerId, setConnectionStatus, showToast).then(() => {
            SetTacoSocketMessageHandler(handleIncomingMessage);
        });

        return () => {
            ClearTacoSocketMessageHandler();
            DisconnectTacoWebSocket();
            if (tapBufferRef.current.timer) clearTimeout(tapBufferRef.current.timer);
        };
    }, [playerId]);

    const handleIncomingMessage = (msg) => {
        const type = (msg?.type || '').toLowerCase();
        const payload = msg?.payload || {};

        if (type === 'taco.progress') {
            setMatchState(prev => ({
                ...prev,
                matchId: payload.matchId,
                phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
                winner: payload.winner
            }));

            // Sincronizamos la UI local con la verdad del servidor
            const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
            if (myStats) {
                setLocalScore(myStats.tacosCompleted);
                setLocalStep(myStats.currentStep);
            }
        } else if (type === 'taco.finished') {
            setMatchState(prev => ({
                ...prev,
                phase: 'FINISHED',
                winner: payload.winner
            }));
        }
    };

    // ==========================================================================
    // SISTEMA DE BATCHING (Envío optimizado de Taps)
    // ==========================================================================
    const canTap = Boolean(playerId && matchState.matchId && matchState.phase === 'RUNNING' && matchState.winner === 'none');

    const flushTapsToServer = async () => {
        const buffer = tapBufferRef.current;
        if (buffer.timer) { clearTimeout(buffer.timer); buffer.timer = null; }
        if (buffer.isSending || buffer.count <= 0 || !matchState.matchId) return;

        const tapsToSend = buffer.count;
        buffer.count = 0;
        buffer.isSending = true;

        try {
            await SendTacoTaps({ matchId: matchState.matchId, playerId, taps: tapsToSend });
        } catch {
            buffer.count += tapsToSend; // Si falla, los regresamos a la cola
        } finally {
            buffer.isSending = false;
        }
    };

    // ==========================================================================
    // LÓGICA DE PARTÍCULAS VISUALES (DOM Directo)
    // ==========================================================================
    const spawnFloatingEmoji = (x, y) => {
        const emoji = document.createElement('div');
        emoji.className = 'taco-fan__emoji';
        emoji.innerText = FLOATING_EMOJIS[Math.floor(Math.random() * FLOATING_EMOJIS.length)];
        emoji.style.setProperty('--target-x', `${(Math.random() - 0.5) * 300}px`);
        emoji.style.setProperty('--target-y', `${-Math.random() * 300 - 50}px`);
        emoji.style.setProperty('--target-rotation', `${(Math.random() - 0.5) * 360}deg`);
        emoji.style.left = `${x}px`;
        emoji.style.top = `${y}px`;
        document.body.appendChild(emoji);
        setTimeout(() => emoji.remove(), 800);
    };

    const spawnTapEffect = (x, y) => {
        const pixel = document.createElement('div');
        pixel.className = 'taco-fan__tap-effect';
        pixel.style.left = `${x}px`;
        pixel.style.top = `${y}px`;
        document.body.appendChild(pixel);
        setTimeout(() => pixel.remove(), 300);
    };

    // ==========================================================================
    // CONTROLADOR PRINCIPAL DEL TAP
    // ==========================================================================
    const handleScreenTap = (event) => {
        if (!canTap) return;

        let tapX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
        let tapY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;

        // 1. Efectos Visuales
        spawnTapEffect(tapX, tapY);
        if (tapY > window.innerHeight / 2) spawnFloatingEmoji(tapX, tapY);

        if (tacoImageRef.current) {
            tacoImageRef.current.style.transform = 'scale(1.15)';
            setTimeout(() => {
                if (tacoImageRef.current && !tacoImageRef.current.classList.contains('taco-fan__image--slide-out')) {
                    tacoImageRef.current.style.transform = 'scale(1)';
                }
            }, 50);
        }

        // 2. Progreso Visual Local Inmediato (Para que no se sienta lag)
        setLocalStep((prev) => {
            if (prev === MAXIMUM_TACO_STEPS) {
                if (tacoImageRef.current) {
                    tacoImageRef.current.classList.add('taco-fan__image--slide-out');
                    setTimeout(() => {
                        if (tacoImageRef.current) {
                            tacoImageRef.current.classList.remove('taco-fan__image--slide-out');
                            tacoImageRef.current.style.transform = 'scale(1)';
                        }
                        setLocalScore(s => s + 1);
                    }, 300);
                }
                return 1;
            }
            return prev + 1;
        });

        // 3. Cola de envíos al servidor
        const buffer = tapBufferRef.current;
        buffer.count++;

        if (buffer.count >= BATCH_SIZE) {
            flushTapsToServer();
            return;
        }

        if (!buffer.timer) {
            buffer.timer = setTimeout(flushTapsToServer, FLUSH_MS);
        }
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    
    // PANTALLA DE SELECCIÓN DE JUGADOR
    if (!playerId) {
        return <SelectPlayer onPick={(p) => { setPlayerId(p); sessionStorage.setItem('taco.player', p); }} />
    }

    const getWinnerMessage = () => {
        if (matchState.winner === 'none') return '';
        if (matchState.winner === 'tie') return '¡ES UN EMPATE!';
        return matchState.winner === playerId ? '¡GANASTE LA RONDA!' : '¡PERDISTE LA RONDA!';
    };

    return (
        <div className="taco-fan" onMouseDown={handleScreenTap} onTouchStart={handleScreenTap}>
            
            {/* CABECERA: Botón de Salir y Estatus */}
            <header style={{ position: 'absolute', top: 0, left: 0, width: '100%', padding: '15px', display: 'flex', flexDirection: 'column', zIndex: 12 }}> {/* justifyContent: 'space-between', */}
                <img src="/img/page/adsl-five.png" className="LogoADSLxZtudia" alt="Logo" />

                <div className='d-flex justify-content-between mt-3'>
                    <button 
                        className="btn btn-danger btn-sm fw-bold" 
                        onClick={() => { setPlayerId(''); sessionStorage.removeItem('taco.player'); }}
                    >
                        <i className="bi bi-box-arrow-right"></i> Salir
                    </button>

                    <div className="text-end">
                        <span className="badge bg-dark small d-block mb-1">{playerId.toUpperCase()}</span>
                        <span className={`badge ${connectionStatus === 'connected' ? 'bg-success' : 'bg-warning'} small d-block`}>
                            {connectionStatus === 'connected' && 'Conectado'}
                        </span>
                    </div>
                </div>
            </header>

            {/* MARCADOR LOCAL */}
            {/* <div style={{ position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center' }}> */}
                {/* <p style={{ color: '#f1c40f', fontSize: '2rem', textShadow: '2px 2px 0 #000', margin: 0 }}>
                    {localScore} TACOS
                </p> */}
                {!canTap && matchState.phase !== 'FINISHED' && (
                    <div className='taco-fan__Set'>
                        <p className='taco-fan__Set__Text'>¡Prepárate!</p>
                    </div>
                )}
            {/* </div> */}

            {/* MITAD SUPERIOR (Taco) */}
            <div className="taco-fan__zone">
                <img 
                    ref={tacoImageRef}
                    src={ASSETS.tacoStepTemplate(localStep)} 
                    alt={`Paso ${localStep}`} 
                    className="taco-fan__image" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* MITAD INFERIOR (Personaje) */}
            <div className="taco-fan__zone">
                <img 
                    src={ASSETS.character} 
                    alt="Empacador" 
                    className="taco-fan__image" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* PANTALLA FINAL */}
            {matchState.phase === 'FINISHED' && (
                <div className="taco-fan__overlay">
                    <div className="taco-fan__note">
                        <div className="taco-fan__result-text">
                            <p style={{ color: matchState.winner === playerId ? '#198754' : '#dc3545', fontSize: '1.2rem', marginBottom: '20px' }}>
                                {getWinnerMessage()}
                            </p>
                            {/* <p>Preparaste</p>
                            <p style={{ fontSize: '2.5rem', color: '#e74c3c', margin: '10px 0' }}>{localScore}</p>
                            <p>tacos en total.</p> */}
                        </div>
                    </div>
                </div>
            )}

            {/* ALERTAS */}
            {/* {toastMessage && (
                <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#198754', color: '#fff', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', zIndex: 100 }}>
                    {toastMessage}
                </div>
            )} */}

            <p className='FooterCredits' style={{ textAlign: 'center', zIndex: '12', color: '#fff', fontSize: '18px' }}>Hecho con ♥ por Interaktia | 2026</p>
        </div>
    );
};

// ==========================================================================
// PANTALLA DE SELECCIÓN (Similar a Tiro a Gol)
// ==========================================================================
const SelectPlayer = ({ onPick }) => {
    return (
        <GoalBase classNameApplied={'Fan__Goal__Header'}>
            <div className="Fan__Goal">
                <p className="Fan__Goal__Title">Selecciona tu personaje</p>
                <div className="Fan__Goal__Players">
                    <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player1')}>
                        <img src='/img/games/taco/barrio_9/lucho-empacador.png' className='Fan__Goal__Players__Buttons__Image' alt="Lucho" />
                        <span className='Fan__Goal__Players__Buttons__Text'>Lucho</span>
                    </button>
                    <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player2')}>
                        <img src='/img/games/taco/barrio_9/tunita-empacadora.png' className='Fan__Goal__Players__Buttons__Image' alt="Tunita" />
                        <span className='Fan__Goal__Players__Buttons__Text'>Tunita</span>
                    </button>
                </div>
            </div>
        </GoalBase>
    );
};

export { Package };